import React from 'react';
import { Pressable, StyleSheet, View } from 'react-native';
import {
  Category,
  RATINGS_ARE_INVENTED,
  Service,
} from '../../data/services';
import { colors, hairline, radii, spacing } from '../../theme';
import { useAreaAvailability } from '../../hooks/useAreaAvailability';
import Icon from '../ui/Icon';
import Text from '../ui/Text';
import ArtPanel from './ArtPanel';
import { cardDuration, cardPrice, cardWasPrice } from '../../data/hourly';

/** Whole rupees, grouped - `Intl` is not reliably present on Android JSC. */
const rupees = (value: number) =>
  '₹' + String(value).replace(/\B(?=(\d{3})+(?!\d))/g, ',');

type Props = {
  service: Service;
  category: Category;
  width: number;
  onPress?: (service: Service) => void;
};

/**
 * One bookable service: picture, name, rating, price and expected duration.
 *
 * Deliberately spare - no description, no duration, and no add control. All
 * three belong on the service's own sheet; on a card in a scrolling rail they
 * crowd out the three things that actually drive the tap.
 */
function ServiceCard({ service, category, width, onPress }: Props) {
  const free = service.price === 0;
  const was = cardWasPrice(service, rupees);
  /* Not offered at this address - see `useAreaAvailability`. Still pressable:
     the tap opens the "coming soon" sheet rather than doing nothing. */
  const unavailable = useAreaAvailability().services.has(service.key);

  return (
    <Pressable
      accessibilityRole="button"
      accessibilityHint={unavailable ? 'Coming soon to this area' : undefined}
      accessibilityLabel={
        `${service.name}, ` +
        /*
          The same guard the star below carries, and it resolves to SILENCE
          rather than to "not yet rated".

          Two reasons, and the first is the serious one. Announcing "rated 4.8
          from 12K reviews" while the card deliberately shows no star would
          make a screen reader the one surface still quoting a made-up figure -
          the worst place for it to survive, because nobody sighted could see
          it was gone and check.

          The second is that the card says nothing about ratings now, so a
          label that mentions them describes a card that does not exist. A
          screen reader should read what is there.
        */
        (RATINGS_ARE_INVENTED || service.rating === undefined
          ? ''
          : `rated ${service.rating} from ${service.reviews} reviews, `) +
        // Read out the same way it is drawn - an hourly service announced as
        // a flat price is the one place a screen reader would mislead worse
        // than the screen does.
        `${free ? 'free' : cardPrice(service, rupees)}, ${cardDuration(service)}`
      }
      onPress={() => onPress?.(service)}
      style={({ pressed }) => [styles.card, { width }, pressed ? styles.pressed : null]}
    >
      <ArtPanel
        image={service.image}
        icon={category.icon}
        style={unavailable ? [styles.art, styles.dim] : styles.art}
      />
      {unavailable ? (
        /* Over the picture, where the eye lands first. One short line on a
           dark pill reads on any photograph and never wraps. */
        <View style={styles.soon}>
          <Text variant="eyebrow" color={colors.onPrimary}>
            COMING SOON
          </Text>
        </View>
      ) : null}

      <View style={[styles.body, unavailable ? styles.dim : null]}>
        {/* Name and rating share a line: the name is what identifies the card
            and the rating is what qualifies it, so they belong together. */}
        <View style={styles.titleRow}>
          <Text variant="subtitle" numberOfLines={2} style={styles.name}>
            {service.name}
          </Text>

          {/*
            ---------------------------------------------------------------
            THE RATING IS INVENTED, SO IT IS NOT SHOWN
            ---------------------------------------------------------------
            This read: *"Omitted, not zeroed, when the catalogue has no rating
            for this service. A card with no stars reads as 'nobody has rated
            this yet'; a 0.0 reads as 'everybody hated it'."* - which is a good
            rule about a number that does not exist.

            `Service.rating` is a hand-written figure in `data/services.ts`,
            attached to a live service by matching its NAME. The platform has
            no ratings at all: nothing aggregates them and no endpoint returns
            them. It has stayed off screen only because none of the fifteen
            services actually being sold has a name in that file - a rename is
            all it would take to put an invented 4.78 on a card.

            `false &&` rather than a deletion: the markup is intact and the
            day real ratings exist, dropping the guard restores it. The same
            wire is cut on the detail sheet - see RATINGS_ARE_INVENTED in
            `ServiceScreen`.
          */}
          {!RATINGS_ARE_INVENTED && service.rating !== undefined && (
            <View style={styles.rating}>
              <Icon name="starFilled" size={12} color={colors.rating} />
              <Text variant="caption">{service.rating.toFixed(1)}</Text>
            </View>
          )}
        </View>

        {/* Price and how long it takes, on one line: the two things somebody
            weighs against each other before tapping. */}
        <View style={styles.footer}>
          {/*
            Wrapped, because the footer is `space-between`: a third child would
            spread the struck figure, the price and the duration evenly across
            the card and break the pairing the eye needs. Struck first and
            price second - the order it is read in, and the one that leaves the
            price as the answer rather than the question.
          */}
          <View style={styles.prices}>
            {was ? (
              <Text
                variant="caption"
                color={colors.inkMuted}
                style={styles.was}
              >
                {was}
              </Text>
            ) : null}
            <Text variant="label">{cardPrice(service, rupees)}</Text>
          </View>

          <View style={styles.duration}>
            <Icon name="clock" size={12} color={colors.inkMuted} />
            <Text variant="caption">{service.minutes} min</Text>
          </View>
        </View>
      </View>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  /* Opacity rather than `filter` - drawn the same on every Android. */
  dim: {
    opacity: 0.45,
  },
  soon: {
    position: 'absolute',
    top: spacing.sm,
    left: spacing.sm,
    paddingHorizontal: spacing.sm,
    paddingVertical: 2,
    borderRadius: radii.md,
    backgroundColor: colors.surfaceInverse,
  },
  card: {
    borderRadius: radii.lg,
    backgroundColor: colors.surface,
    overflow: 'hidden',
    ...hairline,
  },
  /*
   * Shaped to the photography rather than given a fixed height. The tiles run
   * from 1.30 to 1.52 wide-to-tall around a mean of 1.44, so a box at 1.45
   * trims at most about a tenth off the widest or tallest of them; a fixed
   * height would have cropped far more from some and letterboxed others.
   */
  art: {
    width: '100%',
    aspectRatio: 1.45,
    borderRadius: 0,
  },
  body: {
    padding: spacing.sm,
  },
  titleRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: spacing.sm,
  },
  /* Two lines' worth, so one- and two-line names keep the prices aligned. */
  name: {
    flex: 1,
    minHeight: 40,
  },
  rating: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 2,
    paddingTop: 2,
  },
  prices: {
    flexDirection: 'row',
    alignItems: 'baseline',
    gap: spacing.xs,
  },
  /* The strike itself. Nothing else in the app crosses text out. */
  was: {
    textDecorationLine: 'line-through',
  },
  footer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.xs,
    marginTop: spacing.xs,
  },
  duration: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 2,
  },
  pressed: {
    opacity: 0.7,
  },
});

/*
 * Memoised. Home mounts three dozen of these, and every unrelated state change
 * on the screen - opening the cart, marking a notification read, switching tab
 * - re-rendered all of them. Its props are a service, a category and a width,
 * all stable references, so the comparison is cheap and almost always skips.
 */
export default React.memo(ServiceCard);
